import officeImg from "@assets/pngs/office.png";
import {
  CheckBadgeBoldIcon,
  DangerCircleIcon,
  DangerIcon,
  handleApiRequest,
  ImageInput,
  Typography,
  useFetchFile,
  useMobile,
  UserTypeEnum,
} from "@cpj-ai/panel-npm-package";
import {
  AuthAccountOrganizationStatusChoices,
  UpdateAvatarMutation,
  UpdateAvatarMutationVariables,
  UploadType,
  useCommonUploadFileMutation,
  useGetOrganizationInfoQuery,
  useGetOrganizationStatisticsQuery,
  useUpdateAvatarMutation,
} from "@graphql/generated";
import { Button, Card } from "@heroui/react";
import { useOrganizationStore } from "@store/useOrganizationStore";
import { useCallback, useMemo } from "react";

interface StatusConfig {
  label: string;
  color: string;
  icon: JSX.Element;
}

const ProfileTopBar = () => {
  const isMobile = useMobile();
  const { data: organization, refetch } = useGetOrganizationInfoQuery();
  const organizationData = organization?.me?.organizationMemberships[0]?.organization;
  const [upload, { loading: uploading }] = useCommonUploadFileMutation();
  const [updateLogo, { loading }] = useUpdateAvatarMutation();
  const { setSelectedOrganization } = useOrganizationStore();
  const { data: statisticsCounts } = useGetOrganizationStatisticsQuery({
    variables: {
      organizationId: organizationData?.id ?? "",
      organizationNotEmptyId: organizationData?.id,
    },
  });
  const { data: avatarUrl } = useFetchFile({
    urlPath: organizationData?.logo?.file,
    userType: UserTypeEnum.ORG,
  });

  const handleUploadFile = useCallback(
    async (file: File | null) => {
      if (!file || !organizationData?.id) return;

      try {
        const uploadResult = await upload({
          variables: {
            file,
            type: UploadType.ORGANIZATION_LOGO,
          },
        });

        const pk = uploadResult?.data?.common.uploadFile?.pk;

        if (!pk) return;

        await handleApiRequest<UpdateAvatarMutation, UpdateAvatarMutationVariables>({
          apiFunction: updateLogo,
          variables: {
            logo: String(pk),
            organizationId: organizationData.id,
          },
          componentName: "ProfileTopBar",
          actionName: "updateLogo",
          onSuccess: async () => {
            const result = await refetch();
            setSelectedOrganization(result.data?.me?.organizationMemberships[0]?.organization);
          },
          successMessage: "Organization logo updated successfully",
          fallbackErrorMessage: "Failed to update organization logo",
          extraErrorContext: {
            organizationId: organizationData.id,
          },
        });
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    },
    [upload, updateLogo, refetch, organizationData?.id, setSelectedOrganization],
  );

  const statistics = useMemo(
    () => [
      {
        title: "Job created",
        value: statisticsCounts?.organization?.jobPosition?.list?.totalCount,
      },
      {
        title: "Interviewed",
        value: [
          statisticsCounts?.organization?.jobPositionAssignmentStatistics?.interviewScheduledCount,
          statisticsCounts?.organization?.jobPositionAssignmentStatistics?.awaitingInterviewDateCount,
          statisticsCounts?.organization?.jobPositionAssignmentStatistics?.interviewDoneCount,
          statisticsCounts?.organization?.jobPositionAssignmentStatistics?.interviewScheduledCount,
          statisticsCounts?.organization?.jobPositionAssignmentStatistics?.rejectedCount,
        ].reduce((acc, curr) => (acc ?? 0) + (curr ?? 0), 0),
      },
      {
        title: "Hired",
        value: statisticsCounts?.organization?.jobPositionAssignmentStatistics?.acceptedCount,
      },
    ],
    [statisticsCounts],
  );

  const renderStatus = () => {
    const ORGANIZATION_STATUS: Record<AuthAccountOrganizationStatusChoices, StatusConfig> = {
      DRAFTED: {
        label: "Drafted",
        color: "text-gray-500",
        icon: <DangerCircleIcon className="stroke-gray-500 w-6 h-6" />,
      },
      SUBMITTED: {
        label: "Awaiting approval",
        color: "text-blue-500",
        icon: <DangerCircleIcon className="stroke-blue-500 w-6 h-6" />,
      },
      REJECTED: {
        label: "Rejected",
        color: "text-red-500",
        icon: <DangerIcon className=" w-6 h-6" fill={"#ef4444"} />,
      },
      VERIFIED: {
        label: "Verified",
        color: "text-green-500",
        icon: <CheckBadgeBoldIcon className="fill-green-500 w-6 h-6" />,
      },
      BLOCKED: {
        label: "Suspend",
        color: "text-red-500",
        icon: <DangerCircleIcon className="stroke-red-500 w-6 h-6" />,
      },
      COMPLETED: {
        label: "Information Completed",
        color: "text-yellow-500",
        icon: <CheckBadgeBoldIcon className="fill-yellow-500 w-6 h-6" />,
      },
    };
    const status = organizationData?.status;

    if (status === undefined || status === null) {
      return (
        <Typography variant="small" className="bg-yellow-500 text-white rounded-lg p-1 px-2">
          Checking your verify ...
        </Typography>
      );
    }

    const statusConfig = organizationData?.status ? ORGANIZATION_STATUS[status] : null;

    return (
      <div className="flex items-center gap-1">
        {statusConfig?.icon}
        <Typography variant="small" className={`rounded-lg font-bold ${statusConfig?.color}`}>
          {statusConfig?.label}
        </Typography>
      </div>
    );
  };

  const Container = isMobile ? "div" : Card;

  return (
    <div className="relative">
      <div className="w-full h-48 relative overflow-hidden hidden md:block">
        <img src={officeImg} className="w-full h-full object-cover absolute top-0 left-0" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
      </div>

      <Container className="flex flex-col md:mx-7 p-4 gap-6 md:gap-0 md:flex-row md:items-center justify-between pt-8 md:pt-4 md:-mt-14">
        <div className="flex items-center gap-4">
          <ImageInput
            key={avatarUrl}
            layout="avatar"
            value={avatarUrl}
            onChange={handleUploadFile}
            loading={loading || uploading}
          />

          <div className="w-full">
            <div className="flex items-center justify-between md:justify-start w-full gap-2">
              <p className="md:text-2xl font-semibold md:font-bold">{organizationData?.name || "-"}</p>
              <div className="flex">{renderStatus()}</div>
            </div>
            <p className="text-sm md:text-lg md:font-medium text-zinc-500">
              {organizationData?.businessType?.toLowerCase() || "-"}
            </p>
          </div>
        </div>

        <div className="flex gap-9">
          {statistics.map(({ title, value }) => (
            <div key={title} className="flex flex-col items-center justify-center gap-1">
              <p className="text-xs text-zinc-700">{title} :</p>
              <p className="text-lg font-bold">{value}</p>
            </div>
          ))}
        </div>

        <Button isDisabled={true} color="primary" variant="bordered" className="rounded-xl md:me-5">
          Visit Company page
        </Button>
      </Container>
    </div>
  );
};

export default ProfileTopBar;
